import googleIcon from "@/assets/images/google.png";
import microsoftIcon from "@/assets/images/microsoft.png";
import { AgreementLinks } from "@/components/common/AgreementLinks";
import { Logo } from "@/components/common/Logo";
import { loginWithEmail } from "@/services/api/auth";
import { getUserProfileApi } from "@/services/api/profile";
import { useAuthStore } from "@/stores/useAuthStore";
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  LockOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { App, Button, Form, Input } from "antd";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import "./Login.less";

const Login: React.FC = () => {
  const { t } = useTranslation();
  const { message: messageApi } = App.useApp();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [agree, setAgree] = useState(false);

  const handleSubmit = async (values: { email: string; password: string }) => {
    if (!agree) {
      messageApi.warning(t("auth.common.validation_agree_required"));
      return;
    }

    try {
      setLoading(true);
      const payload = {
        email: values.email,
        password: values.password,
      };

      const res = await loginWithEmail(payload);
      const token =
        (res as any)?.data?.token || (res as any)?.data?.data?.token;

      if (token) {
        localStorage.setItem("token", token);

        // 同步 token 到 store,确保 Header 组件能检测到登录状态
        const { setToken, setUser } = useAuthStore.getState();
        setToken(token);

        messageApi.success(t("auth.common.message_login_success"));

        // 获取用户信息
        try {
          const user_id =
            (res as any)?.data?.user?.id ?? (res as any)?.data?.data?.user?.id;
          if (user_id) {
            localStorage.setItem("user_id", user_id);
            const profileResponse = await getUserProfileApi({ user_id });
            if (profileResponse?.code === 200 && profileResponse.data) {
              localStorage.setItem(
                "audit_status",
                profileResponse.data.beta_data?.audit_status?.toString() || ""
              );
              setUser(profileResponse.data);
            }
          }
        } catch (error) {
          console.error("获取用户信息失败:", error);
        }

        // 跳转到首页
        navigate("/");
      }
    } catch (error: any) {
      messageApi.error(error?.message || t("auth.common.message_login_failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = (provider: "google" | "microsoft") => {
    const providerName = provider === "google" ? "Google" : "Microsoft";
    messageApi.info(
      t("auth.common.message_social_coming_soon", { provider: providerName })
    );
    // TODO: 实现社交登录
  };

  return (
    <>
      {/* Logo 和标题 */}
      <div className="login-header">
        <div className="login-logo">
          {/* 品牌Logo */}
          <Logo height={50} />
        </div>
        <div className="login-tagline">{t("auth.common.tagline")}</div>
      </div>

      {/* 登录表单 */}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="login-form"
        size="large"
      >
        {/* 企业邮箱输入 */}
        <Form.Item
          name="email"
          label={t("auth.common.label_corporate_email")}
          rules={[
            {
              required: true,
              message: t("auth.common.validation_email_required"),
            },
            {
              type: "email",
              message: t("auth.common.validation_email_invalid"),
            },
          ]}
        >
          <Input
            prefix={<MailOutlined className="input-icon" />}
            placeholder={t("auth.common.placeholder_email")}
            className="login-input"
          />
        </Form.Item>

        {/* 密码输入 */}
        <Form.Item
          name="password"
          label={
            <div className="password-label">
              <span>{t("auth.common.label_password")}</span>
              <Link to="/auth/reset-password" className="forgot-password">
                {t("auth.common.forgot_password")}
              </Link>
            </div>
          }
          rules={[
            {
              required: true,
              message: t("auth.common.validation_password_required"),
            },
            { min: 6, message: t("auth.common.validation_password_min") },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined className="input-icon" />}
            placeholder={t("auth.common.placeholder_password")}
            className="login-input"
            iconRender={(visible) =>
              visible ? (
                <EyeOutlined className="password-toggle" />
              ) : (
                <EyeInvisibleOutlined className="password-toggle" />
              )
            }
          />
        </Form.Item>

        {/* 条款复选框 */}
        <Form.Item className="agreement-item">
          <div className="agreement-wrapper">
            <AgreementLinks
              showCheckbox={true}
              checked={agree}
              onCheckboxChange={setAgree}
              prefixText={t("auth.common.agree_prefix")}
              colors={{
                primary: "#21C4B8",
                primaryDark: "#4BA67A",
              }}
            />
          </div>
        </Form.Item>

        {/* 登录按钮 */}
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            className="login-button"
            block
          >
            {t("auth.login.btn_login")}
          </Button>
        </Form.Item>
      </Form>

      {/* 注册链接 */}
      <div className="signup-link">
        {t("auth.login.signup_link_prefix")}{" "}
        <Link to="/auth/register">{t("auth.login.signup_link")}</Link>
      </div>

      {/* 社交登录分隔符 */}
      {/* <div className="social-divider">
        <span>{t("auth.common.social_divider")}</span>
      </div> */}

      {/* 社交登录按钮 */}
      {/* <div className="social-login">
        <Button
          className="social-button google-button"
          onClick={() => handleSocialLogin("google")}
        >
          <img src={googleIcon} alt="Google" className="social-icon" />
          <span>{t("auth.common.google")}</span>
        </Button>
        <Button
          className="social-button microsoft-button"
          onClick={() => handleSocialLogin("microsoft")}
        >
          <img src={microsoftIcon} alt="Microsoft" className="social-icon" />
          <span>{t("auth.common.microsoft")}</span>
        </Button>
      </div> */}
    </>
  );
};

export default Login;
